/**
 * RAG Service - Knowledge Base Search API (CCE-011)
 * 
 * Provides API abstraction for knowledge base search with classification facets.
 * Handles query parameter serialization and error handling.
 * 
 * Created: February 4, 2026
 * Epic: CCE - Content Classification Enhancement
 */

import { ApiError, apiFetch } from '@/lib/api';

export interface FacetFilters {
  journey_stage?: string;
  audience_level?: string;
  audience_domain?: string;
  industry?: string;
  technical_depth?: string;
  content_type?: string;
  subject_discipline?: string;
  content_visibility?: string;
  time_sensitivity?: string;
  min_confidence?: number;
}

export interface SearchResult {
  document: string;
  metadata: {
    classifications?: {
      journey_stage?: { value: string; confidence: number };
      audience_level?: { value: string; confidence: number };
      audience_domain?: { value: string; confidence: number };
      industry?: { value: string; confidence: number };
      technical_depth?: { value: string; confidence: number };
      content_type?: { value: string; confidence: number };
      subject_discipline?: { value: string; confidence: number };
      content_visibility?: { value: string; confidence: number };
      time_sensitivity?: { value: string; confidence: number };
    };
    [key: string]: any;
  };
  distance: number;
}

export interface SearchResponse {
  query: string;
  filters: FacetFilters;
  results: SearchResult[];
  count: number;
}

/**
 * Search knowledge base with faceted filtering
 * @param query - Search query string
 * @param facets - Classification facet filters
 * @param nResults - Number of results to return (default: 5, max: 20)
 * @returns Search results with confidence scores
 */
export const searchKnowledgeBase = async (
  query: string,
  facets: FacetFilters,
  nResults: number = 5
): Promise<SearchResponse> => {
  try {
    // Build query parameters
    const params = new URLSearchParams({ 
      q: query, 
      n_results: nResults.toString() 
    });

    // Add facet filters (only non-empty values)
    Object.entries(facets).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });

    // Make API call
    return await apiFetch(`/api/v1/rag/query?${params.toString()}`) as SearchResponse;
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 400) {
        throw new Error('Invalid search parameters. Please check your filters.');
      }
      if (error.status === 404) {
        throw new Error('Knowledge base not found for this organization.');
      }
      if (error.status === 500) {
        throw new Error('Server error while searching. Please try again.');
      }
    }
    
    // Generic error fallback
    throw new Error('An error occurred while searching the knowledge base.');
  }
};

export default {
  searchKnowledgeBase
};
